/**
 * Tendhouse quiz: "Find your system" (sections/th-quiz.liquid).
 *
 * <th-quiz> shows one screen at a time: the intro, five questions, the email gate and one
 * of three results. Every screen is already in the page; this script records the
 * answers, moves focus to each new screen's heading, swaps in the edition's words and
 * sets the customer tags on the gate's Shopify customer form.
 *
 * Submitting the gate: the answers go to sessionStorage, then the form posts normally and
 * the page reloads. On the reloaded page (form.posted_successfully?) the result is shown
 * from storage. If storage is unavailable, the signup is sent in the background instead
 * and the result shows straight away.
 *
 * No dependencies. Motion lives in the section's CSS, behind prefers-reduced-motion.
 */

const STORAGE_KEY = 'th-quiz-answers';
const QUESTIONS = ['edition', 'notes', 'hardest', 'notion', 'clients'];
const FLOW = ['intro', ...QUESTIONS, 'gate'];
const RESULTS = { organized: 'practice_os', clients: 'canva_bundle', both: 'all_access' };
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Lowercase, hyphenated tag text: "a_little" → "a-little".
 * @param {string} value
 */
const slug = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** @returns {Record<string, string> | null} */
function readStored() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, string>} answers
 * @returns {boolean} Whether the answers were stored.
 */
function writeStored(answers) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    return window.sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function clearStored() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

class ThQuiz extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.config = this.readConfig();
    this.answers = {};
    this.current = 'intro';
    this.submitting = false;
    this.form = this.querySelector('form');

    /** @type {Record<string, Set<string>>} Valid answers per question, read from the markup. */
    this.valid = {};
    for (const key of QUESTIONS) {
      const options = this.querySelectorAll(`[data-screen="${key}"] [data-th-answer]`);
      this.valid[key] = new Set([...options].map((option) => option.getAttribute('data-th-answer') ?? ''));
    }

    this.addEventListener('click', this.onClick, { signal });
    this.form?.addEventListener('submit', this.onSubmit, { signal });
    // Back from the next page restores this one as it was: let the form be sent again.
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) return;
        this.submitting = false;
        this.form?.querySelector('[type="submit"]')?.removeAttribute('aria-disabled');
      },
      { signal }
    );

    const saved = readStored();
    const posted = this.querySelector('[data-th-posted]') !== null;
    const failed = this.querySelector('[data-th-errors]') !== null;

    if (posted) {
      if (saved && this.isComplete(saved)) {
        this.answers = { ...saved };
        this.syncPressed();
        this.showResult();
      } else {
        this.show('done');
      }
      this.keepFocusAfterLoad();
      return;
    }

    if (failed && saved && this.isComplete(saved)) {
      // The form came back with an error (for example, an invalid email): back to the gate.
      this.answers = { ...saved };
      this.syncPressed();
      this.show('gate');
      this.keepFocusAfterLoad();
      return;
    }

    this.show('intro', { focus: false });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    window.clearTimeout(this.advanceTimer);
  }

  /**
   * The reloaded page carries the form's #fragment, and the browser's fragment handling
   * can drop focus back to the page once loading ends. Put it back on the screen's heading
   * then, unless the visitor has already moved focus somewhere.
   */
  keepFocusAfterLoad() {
    if (document.readyState === 'complete') return;
    window.addEventListener(
      'load',
      () =>
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active && active !== document.body && active !== document.documentElement) return;
          this.focusScreen(this.current);
        }),
      { once: true, signal: this.controller?.signal }
    );
  }

  /** @returns {{ editions?: Record<string, { url?: string, price?: string, words?: Record<string, string> }> }} */
  readConfig() {
    const script = this.querySelector('script[data-th-quiz-config]');
    try {
      return JSON.parse(script?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /** @param {Record<string, string>} answers */
  isComplete(answers) {
    return QUESTIONS.every((key) => typeof answers[key] === 'string' && this.valid[key]?.has(answers[key]));
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const answer = target.closest('[data-th-answer]');
    if (answer instanceof HTMLButtonElement) {
      this.choose(answer);
      return;
    }

    if (target.closest('[data-th-start]')) {
      this.show(QUESTIONS[0]);
    } else if (target.closest('[data-th-back]')) {
      const index = FLOW.indexOf(this.current);
      if (index > 0) this.show(FLOW[index - 1]);
    } else if (target.closest('[data-th-restart]')) {
      this.answers = {};
      clearStored();
      this.syncPressed();
      this.show(QUESTIONS[0]);
    }
  };

  /** @param {HTMLButtonElement} button */
  choose(button) {
    const screen = button.closest('[data-screen]');
    const key = screen instanceof HTMLElement ? screen.dataset.screen : undefined;
    const value = button.dataset.thAnswer;
    if (!key || !value || !QUESTIONS.includes(key)) return;

    this.answers[key] = value;
    this.syncPressed();

    const next = FLOW[FLOW.indexOf(key) + 1] ?? 'gate';
    window.clearTimeout(this.advanceTimer);
    // A short beat so the chosen answer shows as selected before the next screen.
    this.advanceTimer = window.setTimeout(() => this.show(next), reducedMotion.matches ? 0 : 160);
  }

  /** Marks each question's chosen answer, so Back shows what was picked. */
  syncPressed() {
    for (const key of QUESTIONS) {
      for (const option of this.querySelectorAll(`[data-screen="${key}"] [data-th-answer]`)) {
        option.setAttribute('aria-pressed', String(option.getAttribute('data-th-answer') === this.answers[key]));
      }
    }
  }

  /**
   * Shows one screen and moves focus to its heading.
   * @param {string} name
   * @param {{ focus?: boolean }} [options]
   */
  show(name, { focus = true } = {}) {
    const screen = this.querySelector(`[data-screen="${name}"]`);
    if (!(screen instanceof HTMLElement)) return;

    if (name === 'gate') this.updateTags();

    this.current = name;
    this.dataset.current = name;
    for (const item of this.querySelectorAll('[data-screen]')) {
      if (item instanceof HTMLElement) item.hidden = item !== screen;
    }

    if (focus) this.focusScreen(name);
  }

  /**
   * Moves focus to a screen's heading and brings the top of the quiz into view if needed.
   * @param {string} name
   */
  focusScreen(name) {
    const screen = this.querySelector(`[data-screen="${name}"]`);
    if (!(screen instanceof HTMLElement) || screen.hidden) return;

    const heading = screen.querySelector('[data-th-focus]') ?? this.querySelector('.th-quiz__title');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });

    const top = this.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.6) {
      this.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  }

  /** The customer tags Klaviyo splits on. */
  tags() {
    const { edition, hardest, notion } = this.answers;
    const tags = ['newsletter', 'quiz'];
    if (hardest && RESULTS[hardest]) tags.push(`quiz-${slug(RESULTS[hardest])}`);
    if (edition) tags.push(`edition-${slug(edition)}`);
    if (notion) tags.push(`notion-${slug(notion)}`);
    return tags.join(', ');
  }

  updateTags() {
    const input = this.form?.querySelector('[data-th-tags]');
    if (input instanceof HTMLInputElement) input.value = this.tags();
  }

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const form = this.form;
    if (!form) return;

    if (this.submitting) {
      event.preventDefault();
      return;
    }

    if (!this.isComplete(this.answers)) {
      event.preventDefault();
      this.show(QUESTIONS[0]);
      return;
    }

    this.updateTags();
    this.submitting = true;

    if (writeStored(this.answers)) {
      // Let the form post. Shopify brings the visitor back here with the result to show.
      form.querySelector('[type="submit"]')?.setAttribute('aria-disabled', 'true');
      return;
    }

    // No storage: the answers wouldn't survive a reload, so post in the background.
    event.preventDefault();
    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
    }).catch(() => {});
    this.showResult();
  };

  /** Fills in the reader's edition, shows the lines that match their answers, then the result. */
  showResult() {
    const answers = this.answers;
    const result = RESULTS[answers.hardest] ?? 'all_access';
    const editions = this.config.editions ?? {};
    const edition = editions[answers.edition] ?? editions.coach ?? {};
    const words = edition.words ?? {};

    for (const element of this.querySelectorAll('[data-th-word]')) {
      const word = words[element.getAttribute('data-th-word') ?? ''];
      if (typeof word === 'string' && word) element.textContent = word;
    }

    for (const element of this.querySelectorAll('[data-th-if]')) {
      if (!(element instanceof HTMLElement)) continue;
      const [key, value] = (element.dataset.thIf ?? '').split(':');
      element.hidden = !key || answers[key] !== value;
    }

    for (const link of this.querySelectorAll('[data-th-edition-link]')) {
      if (edition.url) link.setAttribute('href', edition.url);
    }

    for (const line of this.querySelectorAll('[data-th-edition-price]')) {
      if (!(line instanceof HTMLElement)) continue;
      const slot = line.querySelector('[data-th-price]');
      if (slot && edition.price) {
        slot.textContent = edition.price;
        line.hidden = false;
      } else {
        line.hidden = true;
      }
    }

    this.show(result);
  }
}

if (!customElements.get('th-quiz')) customElements.define('th-quiz', ThQuiz);
